import { Mail, MessagesSquare, FileSignature, KeyRound, Hammer } from "lucide-react";
import { useT } from "@/i18n/LanguageProvider";

export const BuyingTimeline = () => {
    const t = useT();
    const STEPS = [
        {
            icon: Mail,
            n: "01",
            title: t({ de: "Anfrage senden", en: "Send inquiry" }),
            text: t({
                de: "Sie wählen Ihre Wunsch-Wohnung und senden uns über das Formular eine unverbindliche Anfrage.",
                en: "Choose your preferred apartment and send us a non-binding inquiry via the form.",
            }),
        },
        {
            icon: MessagesSquare,
            n: "02",
            title: t({ de: "Persönliches Gespräch", en: "Personal conversation" }),
            text: t({
                de: "Wir melden uns zeitnah, klären Ihre Fragen und vereinbaren einen Termin – telefonisch, vor Ort oder online.",
                en: "We respond promptly, clarify your questions and arrange a meeting – by phone, on site or online.",
            }),
        },
        {
            icon: FileSignature,
            n: "03",
            title: t({ de: "Reservierung", en: "Reservation" }),
            text: t({
                de: (
                    <>
                        Nach Klärung von Finanzierung und Wunsch-Optionen reservieren wir Ihre Wohnung schriftlich. Auf Wunsch
                        vermitteln wir Sie an unseren{" "}
                        <a href="#finanzierung" className="underline decoration-brand-500/40 underline-offset-2 hover:text-brand-600">
                            erfahrenen Finanzierungspartner
                        </a>.
                    </>
                ),
                en: (
                    <>
                        Once financing and options are clarified, we reserve your apartment in writing. On request we connect
                        you to our{" "}
                        <a href="#finanzierung" className="underline decoration-brand-500/40 underline-offset-2 hover:text-brand-600">
                            experienced financing partner
                        </a>.
                    </>
                ),
            }),
        },
        {
            icon: Hammer,
            n: "04",
            title: t({ de: "Notartermin & Bauphase", en: "Notary & build phase" }),
            text: t({
                de: "Beurkundung beim Notar. Während des Baus halten wir Sie zu Fortschritt und Bemusterungs-Terminen auf dem Laufenden.",
                en: "Notarisation. During construction we keep you updated on progress and material selection appointments.",
            }),
        },
        {
            icon: KeyRound,
            n: "05",
            title: t({ de: "Schlüsselübergabe", en: "Handover" }),
            text: t({
                de: "Übergabe Ihrer fertigen Eigentumswohnung im erweiterten Ausbauzustand. Willkommen zuhause.",
                en: "Handover of your finished apartment in extended fit-out condition. Welcome home.",
            }),
        },
    ];
    return (
        <section
            id="ablauf"
            className="bg-white py-24 sm:py-32"
            data-testid="buying-timeline-section"
        >
            <div className="container-page">
                <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
                    <div className="lg:col-span-6">
                        <span className="overline">{t({ de: "10 / 12 — So läuft der Kauf", en: "10 / 12 — How buying works" })}</span>
                        <h2 className="h-section mt-4 text-balance">
                            {t({ de: "Vom ersten Klick", en: "From first click" })}
                            <span className="italic text-brand-500"> {t({ de: "zum eigenen Schlüssel.", en: "to your own keys." })}</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-5 lg:col-start-8">
                        <p className="body-md">
                            {t({
                                de: "Transparenz von Anfang an. Diese fünf Schritte führen Sie durch den gesamten Prozess – wir begleiten Sie persönlich bei jedem davon.",
                                en: "Transparency from the start. These five steps guide you through the whole process – we accompany you personally at each one.",
                            })}
                        </p>
                    </div>
                </div>

                <div className="relative">
                    {/* Connector line (desktop) */}
                    <div className="hidden lg:block absolute top-12 left-12 right-12 h-px bg-stone-200" aria-hidden />
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-stone-200 border border-stone-200 rounded-2xl overflow-hidden lg:bg-transparent lg:border-0">
                        {STEPS.map((s, i) => {
                            const Icon = s.icon;
                            return (
                                <div
                                    key={s.n}
                                    className="bg-cream lg:bg-white p-7 group transition-colors hover:bg-clay-50 relative"
                                    data-testid={`timeline-step-${s.n}`}
                                >
                                    <div className="relative w-12 h-12 mb-5 flex items-center justify-center bg-brand-500 text-white group-hover:bg-ink transition-colors">
                                        <Icon size={20} strokeWidth={1.5} />
                                    </div>
                                    <div className="font-heading text-xs text-stone-400 tracking-[0.3em] mb-2">
                                        {t({ de: "SCHRITT", en: "STEP" })} {s.n}
                                    </div>
                                    <h3 className="font-heading text-xl text-ink mb-2 leading-tight">
                                        {s.title}
                                    </h3>
                                    <p className="font-body text-sm text-stone-600 leading-relaxed">
                                        {s.text}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
};
